/**
 * Shared delivery estimate engine.
 *
 * The `custom.shipping_estimate_*` variant metafields hold business day windows ("3-5" or "5").
 * Every surface that quotes a wait — the CRO badge and the variant dropdown status labels — runs
 * those windows through here, so the two can never disagree about the same variant.
 *
 * The projection applies the shipping calendar held in the `estimated_delivery_date_settings`
 * metaobject: the cut-off hour, the holiday list, and whether Saturdays and Sundays ship. A
 * business day window is therefore not the answer a shopper wants — "3-5" posted on a Saturday
 * evening lands 6 to 10 calendar days out. Because that depends on the current time, it is
 * resolved in the browser and never in Liquid, whose output is cached.
 */
window.EddEstimate = window.EddEstimate || (function () {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const LOCALE = 'en-US';
  const TIME_ZONE = 'America/Los_Angeles';

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + parseInt(days, 10));
    return result;
  };

  const formatDate = (date) => date.toLocaleDateString(LOCALE, {
    timeZone: TIME_ZONE,
    month: 'short',
    day: 'numeric'
  });

  const formatRange = (min, max) => (min === max ? `${min}` : `${min} - ${max}`);

  const daysBetween = (from, to) => Math.max(1, Math.round((to - from) / MS_PER_DAY));

  class EddCalendar {
    constructor(settings) {
      const config = settings || {};

      this.cutoffTime = config.cutoffTime || 14;
      this.saturdayShipping = config.saturdayShipping || false;
      this.sundayShipping = config.sundayShipping || false;
      this.holidays = new Set(config.holidays || []);
      this.excludedProducts = config.excludedProducts || [];
      this.today = new Date();
    }

    /** Hour of the day in the shop's timezone, for the cut-off comparison. */
    currentHour() {
      const time = new Date().toLocaleString(LOCALE, {
        timeZone: TIME_ZONE,
        timeStyle: 'short',
        hour12: false
      });
      return parseInt(time.split(':')[0], 10);
    }

    /** Days nothing leaves the warehouse, so the window has to step over them. */
    isClosed(date) {
      return this.holidays.has(date.toISOString().slice(0, 10))
        || (!this.sundayShipping && date.getDay() === 0)
        || (!this.saturdayShipping && date.getDay() === 6);
    }

    /** "3-5" or "5" from a metafield into a business day window. */
    parseWindow(raw) {
      const text = (raw === null || raw === undefined ? '' : raw).toString().trim();
      if (!text) return null;

      const parts = text.split('-');
      const min = parseInt(parts[0], 10);
      const max = parseInt(parts.length > 1 ? parts[1] : parts[0], 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return null;

      return { min, max };
    }

    /** Business days from `start` to the projected shipping date. */
    shipDate(start, businessDays, applyCutoff) {
      let diff = parseInt(businessDays, 10);
      if (Number.isNaN(diff)) return null;

      if (applyCutoff !== false && this.currentHour() >= this.cutoffTime) {
        diff++;
      }

      let index = 0;
      while (index <= diff) {
        if (this.isClosed(addDays(start, index))) {
          diff++;
        }
        index++;
      }

      return addDays(start, diff);
    }

    /** A metafield window straight to its projected date range. */
    dateRange(raw, options) {
      const config = options || {};
      const window = this.parseWindow(raw);
      if (!window) return null;

      const start = config.start || this.today;
      const minDate = this.shipDate(start, window.min, config.applyCutoff);
      const maxDate = window.min === window.max ? minDate : this.shipDate(start, window.max, config.applyCutoff);

      return minDate && maxDate ? { minDate, maxDate } : null;
    }

    daysFromToday(date) {
      return daysBetween(this.today, date);
    }

    /** 'date' | 'days' | 'weeks', counted from today to the projected dates. */
    format(format, minDate, maxDate) {
      switch (format) {
        case 'days': {
          const min = this.daysFromToday(minDate);
          const max = this.daysFromToday(maxDate);
          return `${formatRange(min, max)} ${max === 1 ? 'day' : 'days'}`;
        }
        case 'weeks': {
          const min = Math.ceil(this.daysFromToday(minDate) / 7);
          const max = Math.ceil(this.daysFromToday(maxDate) / 7);
          return `${formatRange(min, max)} ${max === 1 ? 'week' : 'weeks'}`;
        }
        default:
          return formatRange(formatDate(minDate), formatDate(maxDate));
      }
    }
  }

  return {
    create(settings) {
      return new EddCalendar(settings);
    },
    /** Reads one of the url_encode'd JSON payloads Liquid hands over in a data attribute. */
    decode(encoded, fallback) {
      try {
        return JSON.parse(decodeURIComponent(encoded || '').replace(/\+/g, ' '));
      } catch (error) {
        return fallback === undefined ? {} : fallback;
      }
    },
    addDays,
    formatDate,
    formatRange,
    daysBetween
  };
})();
